import { Translate, Validate } from '@dreryk/react-common';
import { appendTestableComponentAttributes, StyledFlex } from '@dreryk/react-components';

import { DosageTimeOfDay } from '~libs.shared.prescription';

import { prescriptionTranslationKeys } from '../../../../../../../translations';
import { ExtendedDosageFormBean, ExtendedDosageFormController } from '../ExtendedDosageForm.types';
import { DOSAGE_TIMES_OF_DAY } from '../../extendedDosage.utils';

import { PortionNumberInput } from './PortionNumberInput';
import { StyledTimeOfDayLabel, StyledTimeOfDayPortionField } from './TimeOfDayPortionField.styled';
import { TimeOfDayIcon } from './TimeOfDayIcon';
import { createStageFieldNameFactory } from './StageFieldList.utils';

const NOT_ADMINISTERED_PORTION = 0;

type TimeOfDayPortionFieldProps = Pick<ExtendedDosageFormController, 'register' | 'watch'> & {
   readonly index: number;
   readonly timeOfDay: DosageTimeOfDay;
   readonly timeOfDayIndex: number;
   readonly defaultValue: number;
   readonly portionUnit: string | null;
};

export const TimeOfDayPortionField = (props: TimeOfDayPortionFieldProps): JSX.Element => {
   const createFieldName = createStageFieldNameFactory(props.index);
   const portionName = createFieldName(`portions[${props.timeOfDayIndex}].portion`);
   const dimmed = Number(props.watch(portionName) ?? props.defaultValue) === NOT_ADMINISTERED_PORTION;
   const portionNames = DOSAGE_TIMES_OF_DAY.map((timeOfDay, index) => createFieldName(`portions[${index}].portion`));

   const validateCyclePortions: Validate<ExtendedDosageFormBean> = (value, getValue, triggerValidation) => {
      const ownPortion = Number(value ?? 0);
      const portionsSum = portionNames.reduce(
         (sum, name) => sum + (name === portionName ? ownPortion : Number(getValue(name) ?? 0)),
         0
      );

      if (ownPortion > 0 && ownPortion === portionsSum) {
         triggerValidation(portionNames.filter((name) => name !== portionName));
      }

      return portionsSum > 0;
   };

   return (
      <StyledTimeOfDayPortionField dimmed={dimmed}>
         <StyledFlex direction={'row'} alignItems={'center'} gap={'4px'}>
            <TimeOfDayIcon timeOfDay={props.timeOfDay} />
            <StyledTimeOfDayLabel
               dimmed={dimmed}
               {...appendTestableComponentAttributes('TimeOfDayLabel', props.timeOfDay)}
            >
               <Translate>{prescriptionTranslationKeys.extendedDosage.timeOfDayMap[props.timeOfDay]}</Translate>
            </StyledTimeOfDayLabel>
         </StyledFlex>
         <StyledFlex direction={'row'} alignItems={'center'} gap={'6px'}>
            <PortionNumberInput
               register={props.register}
               name={portionName}
               defaultValue={props.defaultValue}
               testId={`portion-${props.index}-${props.timeOfDay}`}
               zeroAllowed
               validate={validateCyclePortions}
            />
            {props.portionUnit}
         </StyledFlex>
      </StyledTimeOfDayPortionField>
   );
};
